import type { HTMLAttributes, ReactNode } from 'react';
import './Card.css';

export type CardElevation = 'flat' | 'raised' | 'elevated';
export type CardPadding = 'none' | 'sm' | 'md' | 'lg';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** Уровень тени */
  elevation?: CardElevation;
  /** Внутренние отступы */
  padding?: CardPadding;
  /** Hover-эффект (лёгкое поднятие). Используется на интерактивных карточках */
  interactive?: boolean;
  /** Акцентная полоска слева (для AI-блоков и подобного) */
  accent?: 'none' | 'accent' | 'success' | 'warning' | 'danger';
}

/**
 * Контейнер-карточка дизайн-системы.
 *
 * @example
 *   <Card>Контент</Card>
 *   <Card elevation="elevated" padding="lg" interactive>...</Card>
 *   <Card accent="accent">AI-блок</Card>
 */
export function Card({
  elevation = 'flat',
  padding = 'md',
  interactive = false,
  accent = 'none',
  className,
  children,
  ...rest
}: CardProps) {
  const cls = [
    'ds-card',
    `ds-card--${elevation}`,
    `ds-card--p-${padding}`,
    interactive ? 'ds-card--interactive' : '',
    accent !== 'none' ? `ds-card--accent-${accent}` : '',
    className ?? '',
  ].filter(Boolean).join(' ');

  return <div className={cls} {...rest}>{children}</div>;
}

// ─────────────────────────────────────────────────────────
// Слоты для удобной структуры (header / body / footer)
// ─────────────────────────────────────────────────────────

export function CardHeader({ title, subtitle, actions, icon }: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <div className="ds-card__header">
      {icon && <div className="ds-card__icon">{icon}</div>}
      <div className="ds-card__titles">
        <h3 className="ds-card__title">{title}</h3>
        {subtitle && <div className="ds-card__subtitle">{subtitle}</div>}
      </div>
      {actions && <div className="ds-card__actions">{actions}</div>}
    </div>
  );
}

export function CardBody({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={['ds-card__body', className ?? ''].filter(Boolean).join(' ')}>{children}</div>;
}

export function CardFooter({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={['ds-card__footer', className ?? ''].filter(Boolean).join(' ')}>{children}</div>;
}
